import type {Observable, OperatorFunction} from "rxjs";
import {firstValueFrom, ignoreElements, map} from "rxjs";
import {
  type Existing,
  type IdentifiableModel,
  type IdentifiableModelId,
  type IdOf,
  toIdSelectOptions,
} from "../models/base/IdentifiableModel";
import {
  type SelectOption,
  type SelectOptionGroup,
  type SelectOptionGroupKey,
  type SelectOptionLabelProvider,
  type SelectOptions,
  type SelectOptionsGrouping,
  type SelectOptionValueProviders,
  toSelectOptions,
} from "../models/base/SelectOptions";

export async function completionOf(observable: Observable<never>): Promise<void>;
export async function completionOf<T>(observable: Observable<T>): Promise<void>;
export async function completionOf<T>(observable: Observable<T>): Promise<void> {
  await firstValueFrom(observable.pipe(ignoreElements()), {defaultValue: null});
}

export function mapToSelectOptions<TObject extends object, TValue>(
  valueProviders: SelectOptionValueProviders<TObject, TValue>,
): OperatorFunction<readonly TObject[], readonly SelectOption<TValue>[]>;
export function mapToSelectOptions<TObject extends object, TValue, TGroupKey extends SelectOptionGroupKey>(
  valueProviders: SelectOptionValueProviders<TObject, TValue>,
  grouping: SelectOptionsGrouping<TObject, TGroupKey>,
): OperatorFunction<readonly TObject[], readonly SelectOptionGroup<TValue, TGroupKey>[]>;
export function mapToSelectOptions<TObject extends object, TValue, TGroupKey extends SelectOptionGroupKey>(
  valueProviders: SelectOptionValueProviders<TObject, TValue>,
  grouping?: SelectOptionsGrouping<TObject, TGroupKey>,
): OperatorFunction<readonly TObject[], SelectOptions<TValue, TGroupKey>>;
export function mapToSelectOptions<TObject extends object, TValue, TGroupKey extends SelectOptionGroupKey>(
  valueProviders: SelectOptionValueProviders<TObject, TValue>,
  grouping?: SelectOptionsGrouping<TObject, TGroupKey>,
): OperatorFunction<readonly TObject[], SelectOptions<TValue, TGroupKey>> {
  return map(models => toSelectOptions(models, valueProviders, grouping));
}

// We use ts-ignore here, because locally, it sometimes fails, while in CI it works...
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore: It's not, in fact, infinite...
export function mapToIdSelectOptions<
  TModel extends object & IdentifiableModel<TModelId>,
  TModelId extends IdentifiableModelId = IdOf<TModel>,
>(
  labelProvider: SelectOptionLabelProvider<Readonly<Existing<TModel>>>,
): OperatorFunction<readonly Existing<TModel>[], readonly SelectOption<NoInfer<TModelId>>[]>;
export function mapToIdSelectOptions<
  TModel extends object & IdentifiableModel<TModelId>,
  TGroupKey extends SelectOptionGroupKey,
  TModelId extends IdentifiableModelId = IdOf<TModel>,
>(
  labelProvider: SelectOptionLabelProvider<Readonly<Existing<TModel>>>,
  grouping: SelectOptionsGrouping<Readonly<Existing<TModel>>, TGroupKey>,
): OperatorFunction<readonly Existing<TModel>[], readonly SelectOptionGroup<NoInfer<TModelId>, TGroupKey>[]>;
export function mapToIdSelectOptions<
  TModel extends object & IdentifiableModel<TModelId>,
  TGroupKey extends SelectOptionGroupKey | undefined,
  TModelId extends IdentifiableModelId = IdOf<TModel>,
>(
  labelProvider: SelectOptionLabelProvider<Readonly<Existing<TModel>>>,
  grouping?: SelectOptionsGrouping<Readonly<Existing<TModel>>, TGroupKey>,
): OperatorFunction<readonly Existing<TModel>[], SelectOptions<NoInfer<TModelId>, TGroupKey>>;
export function mapToIdSelectOptions<
  TModel extends object & IdentifiableModel<TModelId>,
  TGroupKey extends SelectOptionGroupKey | undefined,
  TModelId extends IdentifiableModelId = IdOf<TModel>
>(
  labelProvider: SelectOptionLabelProvider<Readonly<Existing<TModel>>>,
  grouping?: SelectOptionsGrouping<Readonly<Existing<TModel>>, TGroupKey>,
): OperatorFunction<readonly Existing<TModel>[], SelectOptions<NoInfer<TModelId>, TGroupKey>> {
  return map(models => toIdSelectOptions(models, labelProvider, grouping));
}
